/**
 * Centralized date/time formatting for the SPA.
 *
 * Single source of truth for "how do we show a timestamp to the user?"
 * Always renders in the **viewer's browser-local time zone** (via
 * Intl.DateTimeFormat with no `timeZone` option), so a user in Pacific
 * sees Pacific, a user on a phone in Mountain sees Mountain, and a
 * Capacitor mobile app picks up the device zone for free.
 *
 * Pairs with the backend split documented in
 * Services/TimeZoneService.cs:
 *
 *   - Storage:           UTC at rest (DB columns, audit timestamps)
 *   - Wire format:       ISO-8601 with `Z` suffix from the API
 *   - Server logs/audit: Eastern, via the backend TimeZoneService
 *   - UI display:        browser-local, via this composable
 *
 * Why a composable and not a util? So pages can grab a single `formatDate`
 * function and not have to remember which Intl options to pass each time.
 * Anywhere you'd reach for `new Date(x).toLocaleString()`, use this
 * instead — it keeps the format consistent across the app and gives us
 * one place to change behaviour later (e.g. switching to relative times,
 * adding a tooltip with the absolute time, etc.).
 *
 * Inputs accepted:
 *   - ISO-8601 string (preferred — comes straight from the API)
 *   - Date object
 *   - number (epoch ms)
 *   - null / undefined → returns the configured `empty` placeholder
 */

// DateStyle + TimeFormat are PREFERENCE values owned by useDisplayPrefs —
// import them rather than re-declaring, so Nuxt auto-import has a single source
// (no duplicate-symbol warning). See useDisplayPrefs for the value semantics.
import type { DateStyle, TimeFormat } from './useDisplayPrefs'

type Input = string | number | Date | null | undefined

// `DateStyle` is imported from useDisplayPrefs (above). `short`/`medium`/
// `long`/`full` map 1:1 to Intl `dateStyle` presets; `numeric` (06/07/2026,
// zero-padded) and `iso` (2026-06-07) use explicit fields (not presets).
export type TimeStyle = 'short' | 'medium' | 'long' | 'full'

export interface FormatOptions {
  /** Date portion style. Set to `false` to omit the date. Default: 'medium'. */
  date?: DateStyle | false
  /** Time portion style. Set to `false` to omit the time. Default: 'short'. */
  time?: TimeStyle | false
  /** What to render for null/undefined/invalid inputs. Default: '—'. */
  empty?: string
}

function toDate(value: Input): Date | null {
  if (value == null) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  // A bare calendar date ("YYYY-MM-DD", no time component) is a date-only
  // value, NOT a UTC instant. `new Date('2026-06-10')` parses it as UTC
  // midnight, which renders a day EARLY for viewers west of UTC (Eastern →
  // "Jun 9"). Parse it as LOCAL midnight so a calendar date shows the same
  // day in every zone. Full ISO timestamps (with a time and/or Z) keep their
  // UTC semantics and still convert to browser-local.
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  }
  const d = new Date(value)
  return isNaN(d.getTime()) ? null : d
}

const PRESET_DATE_STYLES = new Set<DateStyle>(['short', 'medium', 'long', 'full'])

// Date portion for a NON-preset style. `iso` → locale-independent YYYY-MM-DD;
// `numeric` → zero-padded numeric in the viewer's locale (US → 06/07/2026).
function customDatePart(d: Date, style: DateStyle): string {
  const locale = style === 'iso' ? 'en-CA' : undefined
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

/**
 * Pure formatter with EXPLICIT style + 12h/24h — does NOT read prefs. Shared by
 * the reactive helpers in {@link useFormatDate} AND by the /settings/display
 * live preview (which must render the user's PENDING, unsaved choice). Preset
 * date styles combine with the time in one Intl pass (keeps the locale-native
 * separator); custom styles (numeric/iso) build the date part separately and
 * append the time, because Intl forbids mixing `dateStyle` with explicit date
 * fields.
 */
export function formatDateExplicit(
  value: Input,
  dateStyle: DateStyle | false,
  timeFormat: TimeFormat,
  opts: { time?: TimeStyle | false; empty?: string } = {}
): string {
  const d = toDate(value)
  if (!d) return opts.empty ?? '—'
  const hour12 = timeFormat === '12h'
  const timeStyle = opts.time === undefined ? 'short' : opts.time

  if (dateStyle === false || PRESET_DATE_STYLES.has(dateStyle)) {
    const fmtOpts: Intl.DateTimeFormatOptions = { hour12 }
    if (dateStyle) fmtOpts.dateStyle = dateStyle as Intl.DateTimeFormatOptions['dateStyle']
    if (timeStyle) fmtOpts.timeStyle = timeStyle as Intl.DateTimeFormatOptions['timeStyle']
    return new Intl.DateTimeFormat(undefined, fmtOpts).format(d)
  }

  const datePart = customDatePart(d, dateStyle)
  if (!timeStyle) return datePart
  const timePart = new Intl.DateTimeFormat(undefined, { timeStyle, hour12 }).format(d)
  return `${datePart}, ${timePart}`
}

export function useFormatDate() {
  // Pull the user's persisted display preferences. The composable is
  // module-scoped, so flipping a toggle on /settings/display causes
  // every component using useFormatDate to re-render with the new
  // format on the next reactive tick (the format options below are
  // reactive via .value reads).
  //
  // Falls back to DEFAULTS during SSR and the brief window between
  // first paint and the BFF fetch resolving. Defaults are 12h / medium
  // — the same shape every existing caller used to render before this
  // composable started honoring prefs.
  const prefs = useDisplayPrefs()

  /**
   * Date + time in the viewer's local zone.
   * Example: "May 21, 2026, 2:32 PM"
   *
   * Honors the user's `timeFormat` preference — passes `hour12` so the
   * 12h/24h choice overrides the browser locale's default (which would
   * otherwise force `en-GB` users into 24h even if they prefer AM/PM).
   */
  function formatDateTime(value: Input, opts: FormatOptions = {}): string {
    // Per-call `opts.date` / `opts.time` always win; otherwise the user's
    // dateStyle pref (and 12h/24h) take over. Delegates to the shared pure
    // formatter so preset + custom (numeric/iso) styles render identically
    // here and in the /settings/display preview.
    const dateStyle = opts.date === undefined ? prefs.state.value.dateStyle : opts.date
    return formatDateExplicit(value, dateStyle, prefs.state.value.timeFormat, { time: opts.time, empty: opts.empty })
  }

  /**
   * Date only, no time. Example: "May 21, 2026"
   */
  function formatDate(value: Input, opts: Omit<FormatOptions, 'time'> = {}): string {
    return formatDateTime(value, { ...opts, time: false })
  }

  /**
   * Time only, no date. Example: "2:32 PM"
   */
  function formatTime(value: Input, opts: Omit<FormatOptions, 'date'> = {}): string {
    return formatDateTime(value, { ...opts, date: false })
  }

  /**
   * Short ISO-like date for sorting/filtering. Always en-CA so it comes
   * out as YYYY-MM-DD regardless of the viewer's locale. Still uses the
   * browser-local zone — handy for filenames or anywhere you want a
   * stable comparable string.
   */
  function formatIsoDate(value: Input, empty = ''): string {
    const d = toDate(value)
    if (!d) return empty
    return new Intl.DateTimeFormat('en-CA').format(d)
  }

  /**
   * Relative time ("3 minutes ago", "in 2 days"). Falls back to absolute
   * if Intl.RelativeTimeFormat is unavailable (older Safari).
   */
  function formatRelative(value: Input, opts: { empty?: string } = {}): string {
    const d = toDate(value)
    if (!d) return opts.empty ?? '—'
    if (typeof Intl.RelativeTimeFormat === 'undefined') return formatDateTime(d)

    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
    const deltaSec = Math.round((d.getTime() - Date.now()) / 1000)
    const abs = Math.abs(deltaSec)

    // Threshold ladder — pick the largest unit that still has a value ≥1
    // in absolute terms so we say "2 days" not "48 hours".
    const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
      ['year',   60 * 60 * 24 * 365],
      ['month',  60 * 60 * 24 * 30],
      ['week',   60 * 60 * 24 * 7],
      ['day',    60 * 60 * 24],
      ['hour',   60 * 60],
      ['minute', 60],
      ['second', 1],
    ]
    for (const [unit, secondsInUnit] of units) {
      if (abs >= secondsInUnit || unit === 'second') {
        return rtf.format(Math.round(deltaSec / secondsInUnit), unit)
      }
    }
    return formatDateTime(d)
  }

  return { formatDateTime, formatDate, formatTime, formatIsoDate, formatRelative }
}
